import { Suspense, useMemo } from 'react';
import { TrendingDown, TrendingUp } from 'lucide-react';

import { formatCurrency } from '../blocos/tipos';
import { CurvaMes, CurvaMeses } from '../charts';
import {
  nomeDoPeriodo,
  serieAcumulada,
  somaDoPeriodo,
  tituloDoPeriodo,
  type Janela,
  type Periodo,
} from './periodo';
import { SeletorPeriodo } from './SeletorPeriodo';
import type { DadosInicio } from './useDadosInicio';
import type { ObjetivoDoMes } from './useObjetivoDoMes';
import { CardBento, Rotulo } from './ui';
import { useMascara } from '../../valores-ocultos/contexto';

/**
 * O gráfico grande do bento — e, desde o v3 (2026-08-25), a caixa que ABSORVE
 * quatro antigas: objetivo/meta (linha tracejada + "faltam" + ritmo), vendas
 * no ano (sub-linha "2026: …"), contratos do mês (sub-linha) e o histórico
 * mensal (o antigo toggle Mês | 6 meses). A régua do redesenho é o número de
 * caixas do 1b — comprimir aqui é o que paga as caixinhas de fora.
 *
 * ── O PERÍODO NÃO É DESTE CARD ──────────────────────────────────────────────
 * Mês | Semestre | Ano vem de fora (HomeDashboard) porque o "Ticket médio"
 * mostra o MESMO controle: clicar em qualquer um dos dois troca os dois, e um
 * estado por card deixaria a tela afirmando duas janelas de tempo ao mesmo
 * tempo. Abre sempre em Mês, e recarregar volta para Mês — o Início é a foto
 * de hoje, não a última preferência de quem passou por aqui.
 *
 * O badge de comparação só existe no Mês: ele compara com o mês anterior NO
 * MESMO PERÍODO (até o mesmo dia), porque 25 dias contra 31 sempre pareceriam
 * queda. Semestre e ano não têm equivalente pedido — e inventar um aqui seria
 * outra conta na tela sem quem a explique.
 */
export const CardProducaoMes = ({
  data,
  objetivo,
  escopoProprio,
  periodo,
  janela,
  onPeriodoChange,
  navigate,
  alto = false,
}: {
  data: DadosInicio;
  /** O alvo DO PERÍODO exibido (mês, semestre ou ano). */
  objetivo: ObjetivoDoMes | null;
  /** true quando a pessoa só vê a própria carteira — muda os rótulos. */
  escopoProprio: boolean;
  periodo: Periodo;
  janela: Janela;
  onPeriodoChange: (periodo: Periodo) => void;
  navigate: (to: string) => void;
  /** Gestão: ocupa 2 linhas da grade (as caixinhas empilham do lado).
   *  Vendedor: 1 linha, emparelhado com a fila. */
  alto?: boolean;
}) => {
  const m = useMascara();
  const total = useMemo(() => somaDoPeriodo(data.vendasPorMes, janela), [data.vendasPorMes, janela]);
  const curvaMeses = useMemo(
    () => serieAcumulada(data.vendasPorMes, janela),
    [data.vendasPorMes, janela],
  );

  const anterior = data.vendasMesAnteriorMesmoPeriodo;
  const varPct =
    periodo === 'mes' && anterior > 0 ? ((total.valor - anterior) * 100) / anterior : null;
  const mesAnteriorLabel = data.monthly[data.monthly.length - 2]?.label ?? 'mês anterior';
  const diaHoje = new Date().getDate();

  const linkDoPeriodo = `/vendas?tab=completed&from=${janela.de}&to=${janela.ate}`;
  const linkDoMes = (chave: string) => {
    const fim = new Date(Number(chave.slice(0, 4)), Number(chave.slice(5, 7)), 0).getDate();
    return `/vendas?tab=completed&from=${chave}-01&to=${chave}-${String(fim).padStart(2, '0')}`;
  };

  // "Objetivo" sempre — decisão do usuário (2026-08-25): "meta" saiu do
  // vocabulário do dashboard, inclusive pra gestão.
  const rotuloAlvo = objetivo
    ? `objetivo ${m(formatCurrency(objetivo.alvo, true))}${
        objetivo.falta > 0 ? ` · faltam ${m(formatCurrency(objetivo.falta, true))}` : ''
      }`
    : undefined;

  return (
    <CardBento col={2} className={`flex flex-col !px-5 !py-4 ${alto ? 'xl:row-span-2' : ''}`}>
      <div className="flex flex-wrap-reverse items-start justify-between gap-3">
        <div className="min-w-0 flex-1 basis-[200px]">
          <Rotulo>{tituloDoPeriodo(janela, escopoProprio)}</Rotulo>
          <button
            type="button"
            onClick={() => navigate(linkDoPeriodo)}
            className="mt-1.5 cursor-pointer text-[26px] font-bold leading-none tracking-[-0.035em] tabular-nums hover:text-primary sm:text-[34px]"
          >
            {m(formatCurrency(total.valor, true))}
          </button>
          <p className="mt-2 flex flex-wrap items-center gap-[7px] text-[12.5px] text-[#6b7480] dark:text-muted-foreground">
            {varPct != null && (
              <>
                <span
                  className={`inline-flex items-center gap-[3px] rounded-full px-2 py-0.5 font-semibold ${
                    varPct >= 0 ? 'bg-[#dcfce7] text-[#166534]' : 'bg-[#fef3c7] text-[#b45309]'
                  }`}
                >
                  {varPct >= 0 ? (
                    <TrendingUp className="h-3 w-3" />
                  ) : (
                    <TrendingDown className="h-3 w-3" />
                  )}
                  {m(`${Math.abs(varPct).toFixed(1).replace('.', ',')}%`)}
                </span>
                <span>
                  vs. {mesAnteriorLabel} até o dia {diaHoje}
                </span>
                <span>·</span>
              </>
            )}
            <span>
              {m(total.count)} {total.count === 1 ? 'cota' : 'cotas'}
            </span>
            {objetivo?.ritmoPct != null && (
              <>
                <span>·</span>
                <span
                  className={
                    objetivo.ritmoPct >= 100
                      ? 'font-semibold text-green-700'
                      : 'font-semibold text-amber-700'
                  }
                >
                  ritmo fecha em {m(`${objetivo.ritmoPct}%`)}
                </span>
              </>
            )}
          </p>
          {!objetivo && (
            /* Sem alvo o gráfico ficava mudo — e a tela parecia faltando
               coisa. Dizer que não há objetivo definido É a informação. */
            <p className="mt-1 text-[11.5px] text-[#b45309]">
              nenhum objetivo definido para o {nomeDoPeriodo(periodo)}
              {escopoProprio ? ' · cadastro em Desafio Pessoal' : ' · cadastro em Campanhas → Resumo'}
            </p>
          )}
          {/* O ano mora aqui desde que a caixa "Vendas no ano" saiu (v3) —
              menos quando o próprio card JÁ é o ano, e repetir o mesmo número
              duas vezes na mesma caixa só faria duvidar dele. */}
          {periodo !== 'ano' && (
            <button
              type="button"
              onClick={() =>
                navigate(
                  `/vendas?tab=completed&from=${janela.ano}-01-01&to=${janela.ano}-12-31`,
                )
              }
              className="mt-1.5 cursor-pointer text-[11.5px] text-[#6b7480] hover:text-primary dark:text-muted-foreground"
            >
              {janela.ano}:{' '}
              <span className="font-semibold text-foreground">
                {m(formatCurrency(data.vendasAnoValor, true))}
              </span>{' '}
              · {m(data.vendasAnoCount)} {data.vendasAnoCount === 1 ? 'cota' : 'cotas'}
            </button>
          )}
        </div>
        {/* Mês = corrida até o alvo dia a dia; semestre e ano = a mesma
            corrida com os meses no eixo. Três leituras, uma caixa. */}
        <SeletorPeriodo valor={periodo} onChange={onPeriodoChange} />
      </div>
      <div className="mt-2 min-h-[170px] flex-1">
        <Suspense fallback={<div style={{ height: 170 }} />}>
          {periodo === 'mes' ? (
            <CurvaMes
              dados={data.producaoDiaria}
              alvo={objetivo?.alvo ?? null}
              rotuloAlvo={rotuloAlvo}
            />
          ) : (
            <CurvaMeses
              dados={curvaMeses}
              alvo={objetivo?.alvo ?? null}
              rotuloAlvo={rotuloAlvo}
              onSelecionar={(p) => navigate(linkDoMes(p.chave))}
            />
          )}
        </Suspense>
      </div>
    </CardBento>
  );
};
